// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { dappAPI } from '@canton-network/dapp-sdk';
import { ApiException } from '@digitalasset/allocation-api-ts-client';
import {
  EventFormat,
  Filters,
  GetActiveContractsRequest,
  GetUpdatesRequest,
  JsCantonError,
  JsGetActiveContractsResponse,
  JsGetUpdatesResponse,
  TransactionFormatTransactionShapeEnum,
} from '@digitalasset/json-api-ts-client';
import { WSSupport } from '../WSSupport';
import { ApiInstance } from '../types';
import { ACSCacheUtils } from './ACSCacheUtils';
import { ACSContainerLogger } from './ACSContainerLogger';
import { ACSKeyData, ACSUpdateConfig, InitializedACSSet } from './ACSTypes';

export class ACSContainerHttpUtils {
  private static readFromLedgerUsingSdk(api: ApiInstance): api is dappAPI.LedgerApi {
    return typeof api === 'function';
  }

  public static async readUpdates(
    startOffset: number,
    endOffset: number,
    eventFormat: EventFormat,
    api: ApiInstance,
    config: ACSUpdateConfig,
    LOG: ACSContainerLogger | null,
  ): Promise<Array<JsGetUpdatesResponse>> {
    const updates: GetUpdatesRequest = {
      beginExclusive: startOffset,
      endInclusive: endOffset,
      updateFormat: {
        includeTransactions: {
          eventFormat,
          transactionShape: TransactionFormatTransactionShapeEnum.TRANSACTION_SHAPE_ACS_DELTA,
        },
      },
      verbose: false,
    };
    if (ACSContainerHttpUtils.readFromLedgerUsingSdk(api)) {
      return api({
        requestMethod: 'POST',
        resource: '/v2/updates',
        body: JSON.stringify(updates),
      })
        .then((r) => {
          return JSON.parse(r.response) as Array<JsGetUpdatesResponse>;
        })
        .catch((error: Error) => {
          LOG?.gotErrorDuringFetchingACSUpdatesUsingHTTP(error);
          throw error;
        });
    }
    return api.postV2Updates(updates, config.maxUpdatesToFetch, 1000).catch((error: Error) => {
      LOG?.gotErrorDuringFetchingACSUpdatesUsingHTTP(error);
      throw error;
    });
  }

  public static async readACS(
    offset: number,
    key: ACSKeyData,
    api: ApiInstance,
    wsSupport: WSSupport,
    config: ACSUpdateConfig,
    LOG: ACSContainerLogger | null = null,
  ): Promise<InitializedACSSet> {
    if (wsSupport.enabled() && !ACSContainerHttpUtils.readFromLedgerUsingSdk(api)) {
      return ACSContainerHttpUtils.readACSUsingWs(offset, key, wsSupport, config, LOG).catch(() => {
        console.log('Falling back to HTTP for ACS read');
        return ACSContainerHttpUtils.readHttpACS(offset, key, api, wsSupport, LOG);
      });
    }
    return ACSContainerHttpUtils.readHttpACS(offset, key, api, wsSupport, LOG);
  }

  public static createEventFormat(key: ACSKeyData): EventFormat {
    const cumulativeFilter = key.templateId
      ? this.createTemplateFilter(key.templateId)
      : this.createInterfaceFilter(key.interfaceId ?? '');

    if (key.party) {
      return {
        filtersByParty: {
          [key.party]: cumulativeFilter,
        },
        verbose: false,
      };
    }
    return {
      filtersByParty: {},
      filtersForAnyParty: cumulativeFilter,
      verbose: false,
    };
  }

  private static createTemplateFilter(templateId: string): Filters {
    return {
      cumulative: [
        {
          identifierFilter: {
            TemplateFilter: {
              value: {
                templateId,
                includeCreatedEventBlob: false,
              },
            },
          },
        },
      ],
    };
  }

  private static createInterfaceFilter(interfaceId: string): Filters {
    return {
      cumulative: [
        {
          identifierFilter: {
            InterfaceFilter: {
              value: {
                interfaceId,
                includeInterfaceView: true,
                includeCreatedEventBlob: false,
              },
            },
          },
        },
      ],
    };
  }

  private static readACSUsingWs(
    offset: number,
    key: ACSKeyData,
    wsSupport: WSSupport,
    config: ACSUpdateConfig,
    LOG: ACSContainerLogger | null,
  ): Promise<InitializedACSSet> {
    const wsACSURL = `${wsSupport.baseUrl}/v2/state/active-contracts`;
    const format = this.createEventFormat(key);
    const request: GetActiveContractsRequest = {
      activeAtOffset: offset,
      verbose: false,
      eventFormat: format,
    };
    return new Promise<InitializedACSSet>((resolve, reject) => {
      const ws = new WebSocket(wsACSURL, wsSupport.extractProtocols());
      const results: JsGetActiveContractsResponse[] = [];
      let finished = false;
      let error: Error | null = null;
      setTimeout(() => {
        if (!finished && !error && results.length === 0) {
          error = Error(
            `No data received from WebSocket ${wsACSURL} within ${config.wsTimeoutBeforeFirstElement}ms`,
          );
          wsSupport.reportError(error);
          reject(error);
          ws.close();
        }
      }, config.wsTimeoutBeforeFirstElement);
      ws.onopen = () => {
        ws.send(JSON.stringify(request));
      };

      ws.onmessage = (event: MessageEvent<string>) => {
        try {
          const data = JSON.parse(event.data) as JsGetActiveContractsResponse;
          results.push(data);
        } catch (err) {
          console.error('Invalid JSON:', err);
        }
      };

      ws.onerror = () => {
        error = new Error('WebSocket error');
        wsSupport.reportError(error);
        reject(error);
      };

      ws.onclose = () => {
        finished = true;
        if (!error) {
          wsSupport.reportSuccess();
          resolve({
            key: ACSCacheUtils.keyDataToString(key),
            initialized: true,
            creationTimestampMs: Date.now(),
            acsOffset: offset,
            acs: results,
          });
        }
      };
    }).catch((error: Error) => {
      LOG?.gotErrorDuringFetchingEntireACSUsingWebSockets(error);
      throw error;
    });
  }

  private static async readHttpACS(
    offset: number,
    key: ACSKeyData,
    api: ApiInstance,
    wsSupport: WSSupport,
    LOG: ACSContainerLogger | null,
  ): Promise<InitializedACSSet> {
    const format = ACSContainerHttpUtils.createEventFormat(key);

    // 0 means no limit - we handle the case where the result is too large below
    const effectiveQueryLimit = wsSupport.queryLimit === 0 ? undefined : wsSupport.queryLimit;
    // Helper to abstract the API call difference
    const performRequest = async (limit?: number) => {
      const requestBody = {
        activeAtOffset: offset,
        verbose: false,
        eventFormat: format,
      };

      // Path A: Dapp SDK
      if (ACSContainerHttpUtils.readFromLedgerUsingSdk(api)) {
        // Dapp SDK usually takes params in URL string for Get/Post if not in body
        const resource = limit
          ? `/v2/state/active-contracts?limit=${limit}`
          : `/v2/state/active-contracts`;

        return api({
          requestMethod: 'POST',
          resource,
          body: JSON.stringify(requestBody),
        }).then((r) => JSON.parse(r.response) as JsGetActiveContractsResponse[]);
      }

      // Path B: Standard API
      return api.postV2StateActiveContracts(requestBody, limit);
    };

    const acs = await performRequest(effectiveQueryLimit).catch(
      async (error: ApiException<JsCantonError>) => {
        if (error instanceof ApiException) {
          LOG?.gotErrorDuringFetchingEntireACSUsingHTTP(error);
        } else {
          console.error('SDK Error:', error);
        }

        // Handle both strict ApiException and generic SDK errors
        const errorCode =
          error.code || ('status' in error ? (error as { status: string }).status : undefined);
        // this catch clause is specifically to handle the case where the ACS is too large to be returned in one go
        if (errorCode !== 413 || effectiveQueryLimit !== undefined) {
          throw error;
        }

        const errorMessage =
          error?.body?.cause || error?.message || (typeof error === 'string' ? error : '');
        const matchNmb = errorMessage.match(/limit\s*\((\d+)\)/);

        if (!matchNmb || matchNmb.length <= 1) {
          console.log(`Could not extract limit from error message: ${errorMessage}`);
          throw error;
        }

        const serverLimit = parseInt(matchNmb[1], 10);
        if (Number.isNaN(serverLimit)) {
          console.log(`Could not extract server limit from error message: ${errorMessage}`);
          throw error;
        }
        console.error(
          `The result size limit of server ${serverLimit} was exceeded. In order to keep application running limit is added to active-contracts query - this means that potentially not all the data is displayed.`,
        );

        // Update global limit setting
        wsSupport.setQueryLimit(serverLimit);

        // Retry with new limit
        return performRequest(serverLimit);
      },
    );
    return {
      key: ACSCacheUtils.keyDataToString(key),
      initialized: true,
      creationTimestampMs: Date.now(),
      acsOffset: offset,
      acs,
    };
  }
}
